import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang, L } from "@/context/LangContext";
import { groupsForRole, canSeeItem } from "@/components/Layout";

const MAX_FAV = 4;

export function useFavorites() {
  const { user, setUser } = useAuth();
  const favs = user?.favorites || [];
  const toggle = async (to) => {
    const next = favs.includes(to) ? favs.filter((f) => f !== to) : [...favs, to];
    if (next.length > MAX_FAV) { toast.error(`Μέχρι ${MAX_FAV} αγαπημένα — αφαιρέστε ένα πρώτα`); return; }
    const r = await api.put("/me/favorites", { favorites: next });
    setUser({ ...user, favorites: r.data.favorites });
  };
  return { favs, toggle, isFav: (to) => favs.includes(to) };
}

const Tile = ({ to, icon: Icon, label, testid, size = "md", fav, onFav }) => {
  const s = { lg: "p-6 min-h-[140px]", md: "p-5 min-h-[110px]", sm: "p-3.5 min-h-[88px]" }[size];
  const ic = { lg: "h-8 w-8", md: "h-6 w-6", sm: "h-5 w-5" }[size];
  const tx = { lg: "text-lg mt-3", md: "text-sm mt-3", sm: "text-[13px] mt-2" }[size];
  return (
    <div className="relative">
    <Link to={to} data-testid={testid}
      className={`group rounded-2xl bg-white border border-[#E3DED4] hover:border-[#45423C] hover:shadow-md transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 flex flex-col justify-between ${s}`}>
      <Icon className={`${ic} text-[#C86235]`} />
      <span className={`${tx} font-semibold text-[#45423C] leading-tight`}>{label}</span>
    </Link>
    {onFav && (
      <button type="button" data-testid={`fav-${to}`} title={fav ? "Αφαίρεση από αγαπημένα" : "Καρφίτσωμα στα αγαπημένα"}
        onClick={(e) => { e.preventDefault(); onFav(to); }}
        className={`absolute top-2.5 right-2.5 p-1.5 rounded-full transition-colors ${fav ? "text-[#C86235]" : "text-[#D6D0C4] hover:text-[#C86235]"}`}>
        <Star className="h-4 w-4" fill={fav ? "currentColor" : "none"} />
      </button>
    )}
    </div>
  );
};

export function FavoriteTiles() {
  const { favs, toggle } = useFavorites();
  const groups = useMenuGroups();
  const { lang } = useLang();
  const all = groups.flatMap((g) => g.sections.flatMap((s) => s.items));
  const items = favs.map((f) => all.find((i) => i.to === f)).filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="space-y-3" data-testid="favorite-tiles">
      <h2 className="text-xs uppercase tracking-wider font-semibold text-[#8A8577] inline-flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-[#C86235]" fill="currentColor" /> {lang === "el" ? "Αγαπημένα" : "Favorites"}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((i) => <Tile key={i.to} to={i.to} icon={i.icon} label={L(i.label, lang)} testid={`fav-tile-${i.to}`} fav onFav={toggle} />)}
      </div>
    </div>
  );
}

export function useMenuGroups() {
  const { user } = useAuth();
  const canSee = canSeeItem(user);
  return groupsForRole(user?.role).map((g) => ({
    ...g,
    sections: g.subgroups ? g.subgroups.map((s) => ({ ...s, items: s.items.filter(canSee) })).filter((s) => s.items.length)
      : [{ key: g.key, items: g.items.filter(canSee) }].filter((s) => s.items.length),
  })).filter((g) => g.sections.length);
}

export function MenuTiles({ size = "lg" }) {
  const groups = useMenuGroups();
  const { lang } = useLang();
  const t = (x) => L(x, lang);
  const cols = size === "sm" ? "grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-3" : "grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4";
  return (
    <div className={`grid ${cols}`} data-testid="menu-tiles">
      {groups.map((g) => {
        const items = g.sections.flatMap((s) => s.items);
        const to = items.length === 1 && !g.subgroups ? items[0].to : `/menu/${g.key}`;
        return <Tile key={g.key} to={to} icon={g.icon} label={t(g.label)} testid={`menu-tile-${g.key}`} size={size} />;
      })}
    </div>
  );
}

export default function MenuHub() {
  const { key } = useParams();
  const groups = useMenuGroups();
  const { lang } = useLang();
  const { isFav, toggle } = useFavorites();
  const t = (x) => L(x, lang);
  if (!key) {
    return (
      <div className="space-y-6" data-testid="menu-hub">
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C]">{lang === "el" ? "Μενού" : "Menu"}</h1>
        <FavoriteTiles />
        <MenuTiles size="lg" />
      </div>
    );
  }
  const g = groups.find((x) => x.key === key);
  if (!g) return <Navigate to="/" replace />;
  return (
    <div className="space-y-6" data-testid={`menu-group-${key}`}>
      <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] inline-flex items-center gap-3"><g.icon className="h-7 w-7 text-[#C86235]" /> {t(g.label)}</h1>
      {g.sections.map((s) => (
        <div key={s.key} className="space-y-3">
          {g.subgroups && <h2 className="text-xs uppercase tracking-wider font-semibold text-[#8A8577]">{t(s.label)}</h2>}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {s.items.map((i) => <Tile key={i.to} to={i.to} icon={i.icon} label={t(i.label)} testid={`menu-item-${i.to}`} fav={isFav(i.to)} onFav={toggle} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
